import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/client.js';
import {
  enrollLeadIntoCampaign,
  getActiveCampaignMemberships,
} from '../../api/outreach/funnels.js';

const FunnelEnrollModal = ({ isOpen, onClose, leadIds = [], leads = [] }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [activeMemberships, setActiveMemberships] = useState([]);

  const [result, setResult] = useState(null);
  const [autoSent, setAutoSent] = useState(false);

  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setSelectedCampaignId('');
    setResult(null);
    setError(null);
    setAutoSent(false);
    setLoadingCampaigns(true);

    Promise.all([
      apiClient.get('/outreach/campaigns').then(r => r.data?.items || []),
      leadIds.length > 0 ? getActiveCampaignMemberships(leadIds) : Promise.resolve([]),
    ])
      .then(([campaignData, membershipData]) => {
        const eligibleCampaigns = (Array.isArray(campaignData) ? campaignData : [])
          .filter(c => c.status !== 'ARCHIVED' && c.replySequenceId);
        setCampaigns(eligibleCampaigns);
        setActiveMemberships(Array.isArray(membershipData) ? membershipData : []);
      })
      .catch(() => { setCampaigns([]); setActiveMemberships([]); })
      .finally(() => setLoadingCampaigns(false));
  }, [isOpen, leadIds.join(',')]);

  const selectedCampaign = campaigns.find(c => c.id === selectedCampaignId);
  const targetCampaignId = selectedCampaign?.id;
  const skippedLeadIds = useMemo(() => new Set(
    (activeMemberships || [])
      .filter(m => targetCampaignId && m.campaignId === targetCampaignId)
      .map(m => m.leadId)
  ), [activeMemberships, targetCampaignId]);

  const handleEnroll = async () => {
    if (!selectedCampaignId || enrolling) return;
    setEnrolling(true);
    setError(null);

    const validLeadIds = leadIds
      .filter(id => leads.find(l => l.id === id)?.email)
      .filter(id => !skippedLeadIds.has(id));
    if (validLeadIds.length === 0) {
      setError('Все выбранные лиды уже в этой кампании — ничего не добавлено.');
      setEnrolling(false);
      return;
    }

    if (selectedCampaign && !selectedCampaign.requiresCompliment) {
      onClose();
      navigate(`/outreach/campaigns/${selectedCampaignId}/compliments/generate?leadIds=${validLeadIds.join(',')}&namesOnly=1`);
      return;
    }

    try {
      const enrolledIds = [];
      const errors = [];
      let aggregateHasAi = false;
      for (const leadId of validLeadIds) {
        try {
          const data = await enrollLeadIntoCampaign(selectedCampaignId, { leadId });
          enrolledIds.push(leadId);
          if (data?.hasAiVariables) aggregateHasAi = true;
        } catch (err) {
          const respData = err?.response?.data;
          if (err?.response?.status === 409 && respData?.code === 'COMPLIMENT_GATE_REQUIRED') {
            const redirectUrl = respData?.details?.redirectUrl;
            if (redirectUrl) {
              onClose();
              navigate(redirectUrl);
              return;
            }
          }
          errors.push({ leadId, message: err?.message || 'Ошибка записи' });
        }
      }

      if (aggregateHasAi) {
        onClose();
        const params = new URLSearchParams({
          enrollmentIds: enrolledIds.join(','),
          campaignId: selectedCampaignId,
        });
        navigate(`/outreach/ai-verify?${params.toString()}`);
        return;
      }

      setAutoSent(true);
      setResult({ enrolled: enrolledIds.length, errors });
      setStep(3);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Ошибка записи');
    } finally {
      setEnrolling(false);
    }
  };

  if (!isOpen) return null;

  const leadsWithEmail = leads.filter(l => leadIds.includes(l.id) && l.email);
  const eligibleCount = leadsWithEmail.filter(l => !skippedLeadIds.has(l.id)).length;

  return (
    <div className='modal-backdrop' onClick={() => {}}>
      <div className='modal--premium' style={{ maxWidth: '600px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <header className='modal__header--premium'>
          <h2 className='modal__title'>
            {step === 1 && 'Добавить в кампанию'}
            {step === 3 && 'Готово'}
          </h2>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginRight: 12 }}>
            {[1, 3].map(s => (
              <div key={s} style={{
                width: s === step ? 24 : 8,
                height: 8,
                borderRadius: 4,
                background: s <= step ? 'var(--purple-60, #7c3aed)' : 'var(--neutral-40, #e5e7eb)',
                transition: 'all 0.2s',
              }} />
            ))}
          </div>
          <button type='button' className='modal__close-button' onClick={onClose} disabled={enrolling}>✕</button>
        </header>

        <div className='modal__content' style={{ overflowY: 'auto', flex: 1 }}>
          {step === 1 && (
            <div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16 }}>
                Лидов: <strong style={{ color: 'var(--text-primary)' }}>{leadsWithEmail.length}</strong>
                {leadsWithEmail.length < leadIds.length && (
                  <span style={{ color: '#ef4444', marginLeft: 8 }}>
                    ({leadIds.length - leadsWithEmail.length} без email)
                  </span>
                )}
              </p>

              {selectedCampaignId && skippedLeadIds.size > 0 && (
                <div className='preflight-notice' style={{
                  padding: '10px 14px',
                  marginBottom: 12,
                  background: 'rgba(148, 163, 184, 0.08)',
                  border: '1px solid rgba(148, 163, 184, 0.25)',
                  borderRadius: 8,
                  fontSize: 13,
                  color: 'var(--text-secondary)',
                }}>
                  {skippedLeadIds.size} лид(ов) уже в этой кампании — их пропустим
                </div>
              )}

              {selectedCampaignId && leadsWithEmail.length > 0 && (
                <div style={{ marginBottom: 12, maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                  {leadsWithEmail.map(l => {
                    const isSkipped = skippedLeadIds.has(l.id);
                    return (
                      <div
                        key={l.id}
                        className={`lead-row ${isSkipped ? 'lead-row--skipped' : ''}`}
                        style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '6px 12px',
                          fontSize: 12,
                          borderBottom: '1px solid var(--neutral-20, #f0f2f7)',
                          opacity: isSkipped ? 0.5 : 1,
                          textDecoration: isSkipped ? 'line-through' : 'none',
                        }}
                        title={isSkipped ? 'Уже в кампании — пропустим' : undefined}
                      >
                        <span style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {l.name || l.realName || l.instagram || '—'}
                          {isSkipped && (
                            <span className='skip-badge' style={{
                              fontSize: 9, fontWeight: 600, textTransform: 'uppercase',
                              padding: '1px 5px', borderRadius: 3,
                              background: 'rgba(148, 163, 184, 0.18)',
                              color: 'var(--text-muted)',
                              textDecoration: 'none',
                            }}>
                              Пропуск
                            </span>
                          )}
                        </span>
                        <span style={{ color: 'var(--text-muted)' }}>{l.email}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {loadingCampaigns ? (
                <p style={{ color: 'var(--text-muted)' }}>Загрузка...</p>
              ) : campaigns.length === 0 ? (
                <p style={{ color: 'var(--text-muted)' }}>Нет доступных Reply.io-кампаний.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {campaigns.map(c => (
                    <div
                      key={c.id}
                      onClick={() => setSelectedCampaignId(c.id)}
                      style={{
                        padding: '14px 16px',
                        borderRadius: 10,
                        border: `1px solid ${selectedCampaignId === c.id ? 'var(--purple-60, #7c3aed)' : 'var(--border, rgba(148,163,184,0.15))'}`,
                        background: selectedCampaignId === c.id ? 'var(--purple-10, rgba(124,58,237,0.06))' : 'var(--bg-panel)',
                        cursor: 'pointer',
                        transition: 'border-color 0.15s, background 0.15s',
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>
                        {c.name}
                        {c.ownerUser?.name && (
                          <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · {c.ownerUser.name}</span>
                        )}
                      </div>
                      {c.description && (
                        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{c.description}</div>
                      )}
                      <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
                        {c.status && (
                          <span style={{
                            fontSize: 11,
                            padding: '1px 6px',
                            borderRadius: 4,
                            background: c.status === 'ACTIVE' ? 'rgba(34,197,94,0.1)' : 'rgba(251,191,36,0.1)',
                            color: c.status === 'ACTIVE' ? '#16a34a' : '#b45309',
                            fontWeight: 500,
                          }}>
                            {c.status === 'ACTIVE' ? 'Активна' : c.status}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && result && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: 'rgba(34, 197, 94, 0.1)', border: '2px solid rgba(34, 197, 94, 0.3)',
                display: 'grid', placeItems: 'center', margin: '0 auto 16px', fontSize: 24,
                color: '#22c55e',
              }}>
                ✓
              </div>
              <p style={{ fontSize: 16, color: 'var(--text-primary)', fontWeight: 600 }}>
                Записано: {result.enrolled}
              </p>
              {autoSent && (
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>
                  Лиды добавлены в кампанию. Письма отправляются автоматически.
                </p>
              )}
              {result.errors?.length > 0 && (
                <p style={{ fontSize: 13, color: '#ef4444', marginTop: 8 }}>
                  Ошибок: {result.errors.length}
                </p>
              )}
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>
                Кампания: {selectedCampaign?.name}
              </p>
            </div>
          )}
        </div>

        <footer className='modal__footer' style={{ padding: '14px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          {step === 1 && (
            <>
              <button type='button' className='btn btn--ghost btn--sm' onClick={onClose}>Отмена</button>
              <button type='button' className='btn btn--primary btn--sm' onClick={handleEnroll}
                disabled={!selectedCampaignId || enrolling || leadsWithEmail.length === 0 || eligibleCount === 0}>
                {enrolling ? 'Обработка...' : 'Добавить'}
              </button>
            </>
          )}
          {step === 3 && (
            <button type='button' className='btn btn--primary btn--sm' onClick={onClose}>Готово</button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default FunnelEnrollModal;
